import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction';

class CalendarTemplate extends React.Component {
    constructor(props){
        super();

        this.store = props.store;
        this.plugins = [ dayGridPlugin ];
        if(this.store.editable)
            this.plugins.push(interactionPlugin);
        this.calendarRef = React.createRef();
    }

    // Month navigation refetch. v4/early-v6 drove this off the `events` *function*
    // source, but a function source whose identity changes every render makes v6
    // tear down + refetch the whole source on each 60s auto-refresh (flicker +
    // reorder). We now feed `events` as a plain array (in-place RESET_RAW_EVENTS
    // reconcile, stable defId/instanceId) and trigger the navigation refetch here
    // instead — `datesSet` fires on the same range changes the events function used to.
    handleDatesSet = (dateInfo) => {
        var shouldRefetch = this.store.dateRange.from.getMonth() != dateInfo.start.getMonth() ||
                            (this.store.dateRange.from.getMonth() == dateInfo.start.getMonth() && this.store.dateRange.from.getDate() != dateInfo.start.getDate());
        if(shouldRefetch){
            this.store.setDateRange(dateInfo.start, dateInfo.end);
            this.store.setDate(new Date(dateInfo.start.getFullYear(), dateInfo.start.getMonth(), dateInfo.start.getDate() + 10));
            this.store.onEventsFetch();
        }
    }
    eventDidMount = (info) => {
        // v4 fired eventRender (DOM decoration) and eventPositioned (post-layout)
        // separately; v6 unifies both as eventDidMount, fired once after insertion.
        if (this.store.onEventRender) this.store.onEventRender(info);
        if (this.store.onEventPositioned) this.store.onEventPositioned(info);
        // v6 stacks events independently per cell, so a 2-line wrapped event in
        // one column breaks alignment with single-line events in sibling columns
        // at the same row index. v4 rendered events in a shared `.fc-content-skeleton`
        // <tr> per row, which forced every column to share the row's max height.
        // Restore that by syncing per-row heights across columns after all events
        // mount (debounced via rAF so we run once per mount batch, not per event).
        this.scheduleRowHeightSync();
    };

    componentDidUpdate() {
        // Changing `eventOrder` (the "Sort by Account Representative" toggle)
        // re-sorts events into new stack positions but REUSES the existing
        // harness DOM nodes — FullCalendar moves them rather than remounting,
        // so `eventDidMount` does NOT fire and the per-row min-heights applied
        // for the previous order stay stuck on the now-reordered harnesses
        // (rows stop lining up across columns). Re-run the same row-height sync
        // after every update so a pure reorder recalculates spacing exactly
        // like a fresh mount does (rAF-debounced, idempotent, no React state).
        this.scheduleRowHeightSync();
    }

    scheduleRowHeightSync = () => {
        if (this._syncRowsRaf) return;
        this._syncRowsRaf = requestAnimationFrame(() => {
            this._syncRowsRaf = null;
            this.syncRowHeights();
        });
    };

    syncRowHeights = () => {
        // Scope the lookup to this calendar's own root (getApi().el) rather than
        // the whole document, so we never touch another calendar/page content.
        const api = this.calendarRef.current && this.calendarRef.current.getApi();
        const root = api && api.el;
        if (!root) return;
        // Each week row is its own <tr>; events at row-index N in different
        // weeks render at different Y positions, so the sync MUST be scoped
        // per week row. Build a per-week matrix of [column][event] harnesses.
        // `.fc-daygrid-day-events` contains a mix of `.fc-daygrid-event-harness`
        // (actual events) and a trailing `.fc-daygrid-day-bottom` ("show more"
        // placeholder); filter to harnesses only so index-based sync compares
        // like for like across columns.
        const weekRows = Array.from(root.querySelectorAll('tr.fc-daygrid-week, .fc-daygrid-body tbody > tr'));
        const rows = weekRows
            .map(tr => Array.from(tr.querySelectorAll('.fc-daygrid-day-events'))
                .map(col => Array.from(col.querySelectorAll(':scope > .fc-daygrid-event-harness'))))
            .filter(columnHarnesses => columnHarnesses.length > 0);
        if (rows.length === 0) return;
        // Phase 1 (write): clear previously-applied min-heights everywhere first.
        for (const columnHarnesses of rows)
            for (const harnesses of columnHarnesses)
                for (const h of harnesses) h.style.minHeight = '';
        // Phase 2 (read): measure the tallest harness per row-index per week.
        // Done after ALL resets, so the first height read forces a single
        // reflow and no later write invalidates a subsequent read.
        const rowTargets = rows.map(columnHarnesses => {
            const maxRows = Math.max(...columnHarnesses.map(hs => hs.length));
            const heights = [];
            for (let i = 0; i < maxRows; i++) {
                let maxH = 0;
                for (const harnesses of columnHarnesses) {
                    const harness = harnesses[i];
                    // Measure the fractional border-box height, NOT offsetHeight:
                    // offsetHeight rounds up (a 23.66px harness reads as 24), and
                    // that ~0.34px slack per harness stacks into a visibly larger
                    // gap between event pills than develop. The fractional height
                    // keeps min-height equal to the natural height, so the pill-to-
                    // pill gap stays the bare 1px event margin (matches develop 1:1).
                    if (harness) {
                        const h = harness.getBoundingClientRect().height;
                        if (h > maxH) maxH = h;
                    }
                }
                heights[i] = maxH;
            }
            return heights;
        });
        // Phase 3 (write): apply each row-index's max height across its columns.
        rows.forEach((columnHarnesses, r) => {
            rowTargets[r].forEach((maxH, i) => {
                if (maxH === 0) return;
                for (const harnesses of columnHarnesses) {
                    const harness = harnesses[i];
                    if (harness) harness.style.minHeight = maxH + 'px';
                }
            });
        });
    };

    render ()  {
        this.store.events // causes the component to rerender
        return (
            <FullCalendar
                ref={this.calendarRef}
                initialView="dayGridMonth"
                plugins={this.plugins}
                initialDate={this.store.date}
                timeZone='none'
                eventClick={this.store.onEventClick}
                events={this.store.events.slice()}
                datesSet={this.handleDatesSet}
                editable={this.store.editable}
                dragScroll={this.store.editable}
                fixedWeekCount={false}
                weekends={false}
                eventDrop={this.store.onEventDrop}
                height='auto'
                eventDidMount={this.eventDidMount}
                dayCellDidMount={this.store.onDayRender}
                eventDragStart={this.store.onEventDragStart}
                eventDragStop={this.store.onEventDragStop}
                eventOrder={this.store.eventOrder}
                footerToolbar={{ right: 'prev,next' }}
            />
        )
    }
}

CalendarTemplate.propTypes = {
    store: PropTypes.any
};

CalendarTemplate.defaultProps = {
};

export default defaultTemplate(CalendarTemplate);
